import { BatchQueue } from "./batch-queue";
import type { PulseClientConfig } from "./config";
import { normalizeBaseUrl } from "./config";
import { PulseError } from "./errors";
import {
  buildEventPayload,
  chunkEvents,
  MAX_EVENTS_PER_BATCH,
  type PulseEventName,
  type PulseEventPayload,
  type TrackOptions,
} from "./events";
import { pulseRequest } from "./http";

export type TrackBatchItem = { eventName: PulseEventName } & TrackOptions;

export type PulseTrackResponse = {
  accepted: boolean;
  event_id: string;
};

export type PulseBatchResponse = {
  accepted: number;
  rejected: number;
  errors: Array<{ event_id: string | null; code: string; message: string }>;
};

export type GetTenantEventsFullParams = {
  tenantId?: string;
  fromDate?: string;
  toDate?: string;
};

export type TenantDataRow = {
  event_id: string;
  event_name: string;
  application_id: string;
  tenant_id: string;
  occurred_at: string;
  received_at: string;
  event_date: string;
  anonymous_id: string | null;
  user_id: string | null;
  client_browser: string;
  client_device_type: string;
  entity_type: string | null;
  entity_id: string | null;
  ingested_at: string;
  metadata: Record<string, unknown>;
};

export type GetTenantEventsFullResponse = {
  application_id: string;
  tenant_id: string;
  generated_at: string;
  schema_version: string;
  date_range: { from_date: string; to_date: string };
  rows: TenantDataRow[];
};

const DEFAULT_BATCH_MAX_SIZE = 20;
const DEFAULT_BATCH_FLUSH_INTERVAL_MS = 5000;

export class PulseClient {
  protected readonly config: PulseClientConfig;
  private readonly batchEnabled: boolean;
  private readonly queue: BatchQueue<PulseEventPayload> | undefined;

  constructor(config: PulseClientConfig) {
    this.config = { ...config, baseUrl: normalizeBaseUrl(config.baseUrl) };
    this.batchEnabled = config.batch?.enabled ?? false;

    if (this.batchEnabled) {
      const requestedMaxSize = config.batch?.maxSize ?? DEFAULT_BATCH_MAX_SIZE;
      this.queue = new BatchQueue<PulseEventPayload>({
        // Clamp to MAX_EVENTS_PER_BATCH so a flush is always a single chunk in sendBatch().
        // Otherwise, a partial-chunk failure would cause the onFlush requeue below to
        // re-enqueue already-successful chunks too, leading to duplicate delivery.
        maxSize: Math.min(requestedMaxSize, MAX_EVENTS_PER_BATCH),
        flushIntervalMs: config.batch?.flushIntervalMs ?? DEFAULT_BATCH_FLUSH_INTERVAL_MS,
        onFlush: async (events, requeue) => {
          try {
            await this.sendBatch(events);
          } catch (error) {
            if (this.isRetryableFailure(error)) requeue(events);
            this.reportError(error);
          }
        },
      });
    }
  }

  async track(eventName: PulseEventName, options: TrackOptions = {}): Promise<PulseTrackResponse | void> {
    const payload = buildEventPayload(this.config, eventName, options);

    if (!this.batchEnabled || options.immediate) {
      return this.sendSingle(payload);
    }

    this.queue?.enqueue(payload);
    return undefined;
  }

  async trackBatch(items: TrackBatchItem[]): Promise<PulseBatchResponse[]> {
    const payloads = items.map(({ eventName, ...options }) => buildEventPayload(this.config, eventName, options));
    return this.sendBatch(payloads);
  }

  async flush(): Promise<void> {
    await this.queue?.flush();
  }

  async getTenantEventsFull(params: GetTenantEventsFullParams = {}): Promise<GetTenantEventsFullResponse> {
    const tenantId = params.tenantId ?? this.config.tenantId;
    if (!tenantId) {
      throw new PulseError(
        0,
        "invalid_request",
        "tenantId is required: pass params.tenantId or config.tenantId",
      );
    }
    if ((params.fromDate === undefined) !== (params.toDate === undefined)) {
      throw new PulseError(0, "invalid_request", "fromDate and toDate must be provided together");
    }

    return pulseRequest<GetTenantEventsFullResponse>(
      this.config,
      `/v1/tenants/${encodeURIComponent(tenantId)}/events/full`,
      {
        method: "GET",
        query: {
          application_id: this.config.applicationId,
          from_date: params.fromDate,
          to_date: params.toDate,
        },
      },
    );
  }

  private async sendSingle(payload: PulseEventPayload): Promise<PulseTrackResponse> {
    return pulseRequest<PulseTrackResponse>(this.config, "/v1/events", {
      method: "POST",
      body: payload,
    });
  }

  private async sendBatch(payloads: PulseEventPayload[]): Promise<PulseBatchResponse[]> {
    const responses: PulseBatchResponse[] = [];
    for (const chunk of chunkEvents(payloads, MAX_EVENTS_PER_BATCH)) {
      responses.push(
        await pulseRequest<PulseBatchResponse>(this.config, "/v1/events/batch", {
          method: "POST",
          body: { events: chunk },
        }),
      );
    }
    return responses;
  }

  private isRetryableFailure(error: unknown): boolean {
    if (error instanceof PulseError) {
      return error.status === 0 || error.status >= 500;
    }
    return true;
  }

  private reportError(error: unknown): void {
    const pulseError =
      error instanceof PulseError
        ? error
        : new PulseError(0, "network_error", error instanceof Error ? error.message : String(error));
    this.config.onError?.(pulseError);
  }
}

export function createPulse(config: PulseClientConfig): PulseClient {
  return new PulseClient(config);
}
